// ----- Phase status-tracking-drawer (TODO-026) — StatusDrawer -----
//
// Right-side drawer showing lifecycle history for a single submission sequence.
// Replaces the centered LifecycleReportPanel modal with a slide-in drawer.
//
// Data source: GET /api/v1/submissions/:id/lifecycle
// Agency confirmation is a read-only display of the server-owned record.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { v2, type LifecycleEvent, type Submission } from '@/api/v2Client';

export interface StatusDrawerProps {
  /** The submission to show status / lifecycle for. */
  readonly submission: Submission;
  /** Called when the drawer should be closed. */
  readonly onClose: () => void;
}

// ---- Styles ----

const DRAWER_WIDTH = 480;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  zIndex: 900,
};

const drawerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: DRAWER_WIDTH,
  maxWidth: '100vw',
  background: 'var(--surface)',
  boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
  zIndex: 901,
  display: 'flex',
  flexDirection: 'column',
  animation: 'slideInRight 250ms ease-out',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '18px 20px 14px',
  borderBottom: '1px solid var(--border)',
  flexShrink: 0,
};

const scrollBodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '16px 20px',
};

const eventRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  fontSize: 13,
};

const actionBadgeBase: CSSProperties = {
  fontSize: 10,
  padding: '2px 7px',
  borderRadius: 4,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const agencyBlockStyle: CSSProperties = {
  marginTop: 20,
  padding: '14px 16px',
  background: 'var(--surface2)',
  borderRadius: 8,
  border: '1px solid var(--border)',
};

// ---- Helpers (shared with LifecycleReportPanel) ----

function actionIcon(action: string): string {
  if (action.includes('lifecycle')) return '🔄';
  if (action.includes('create') || action.includes('upload')) return '📄';
  if (action.includes('delete')) return '🗑️';
  if (action.includes('update') || action.includes('patch')) return '✏️';
  if (action.includes('reorder')) return '↕️';
  if (action.includes('validate') || action.includes('validation')) return '✅';
  if (action.includes('compile') || action.includes('publish')) return '📦';
  if (action.includes('confirm') || action.includes('approve')) return '✔️';
  return '📋';
}

function actionBadgeStyle(action: string): CSSProperties {
  if (action.includes('lifecycle'))
    return { ...actionBadgeBase, background: '#EFF6FF', color: '#1d4ed8' };
  if (action.includes('delete'))
    return { ...actionBadgeBase, background: 'var(--red-bg, #FEF2F2)', color: 'var(--red, #b91c1c)' };
  if (action.includes('create') || action.includes('upload'))
    return { ...actionBadgeBase, background: 'var(--green-bg, #F0FFF4)', color: 'var(--green, #15803d)' };
  return { ...actionBadgeBase, background: 'var(--surface2)', color: 'var(--text-muted)' };
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDateOnly(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function DiffSummary({ before, after }: Pick<LifecycleEvent, 'before' | 'after'>): ReactElement | null {
  if (!before && !after) return null;
  const lines: string[] = [];
  const allKeys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  for (const key of allKeys) {
    const b = before?.[key];
    const a = after?.[key];
    if (b !== a) lines.push(`${key}: ${String(b ?? '—')} → ${String(a ?? '—')}`);
  }
  if (lines.length === 0) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
      {lines.slice(0, 3).map((l) => <div key={l}>{l}</div>)}
      {lines.length > 3 && <div>…{lines.length - 3} more</div>}
    </div>
  );
}

// ---- Main Component ----

export function StatusDrawer({ submission, onClose }: StatusDrawerProps): ReactElement {
  const [events, setEvents] = useState<readonly LifecycleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const confirmedAt = submission['AgencyConfirmedAt'] as string | null | undefined;

  const drawerRef = useRef<HTMLDivElement>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.submissions.getLifecycleHistory(submission.Id);
      setEvents(res.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lifecycle history');
    } finally {
      setLoading(false);
    }
  }, [submission.Id]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Build header label
  const headerLabel = submission.NdaNumber
    ? `${submission.DrugName} (NDA ${submission.NdaNumber}) — Seq ${submission.SequenceNumber ?? '—'}`
    : `${submission.DrugName} — Seq ${submission.SequenceNumber ?? '—'}`;

  return (
    <>
      {/* Backdrop — click outside closes */}
      <div
        data-testid="status-drawer-backdrop"
        style={backdropStyle}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        data-testid="status-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Status history for ${headerLabel}`}
        style={drawerStyle}
      >
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              {headerLabel}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Status history
            </div>
          </div>
          <button
            type="button"
            data-testid="status-drawer-close"
            onClick={onClose}
            aria-label="Close status drawer"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: '2px 4px',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={scrollBodyStyle}>
          {/* Event timeline */}
          {loading && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Loading history…
            </div>
          )}

          {!loading && error && (
            <div
              role="alert"
              style={{
                color: 'var(--red, #b91c1c)',
                background: 'var(--red-bg, #FEF2F2)',
                padding: '10px 14px',
                borderRadius: 6,
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {error}
              <button
                type="button"
                onClick={() => void fetchHistory()}
                style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div
              data-testid="status-drawer-empty"
              style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}
            >
              No lifecycle events recorded yet.
            </div>
          )}

          {!loading && !error && events.length > 0 && (
            <div data-testid="status-drawer-event-list">
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                {events.length} event{events.length !== 1 ? 's' : ''} · newest first
              </div>
              {events.map((ev) => (
                <div
                  key={ev.id}
                  data-testid={`drawer-event-${ev.id}`}
                  style={eventRowStyle}
                >
                  <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true">
                    {actionIcon(ev.action)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={actionBadgeStyle(ev.action)}>
                        {ev.action}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {ev.targetType} {ev.targetId.slice(0, 8)}…
                      </span>
                    </div>
                    <DiffSummary before={ev.before} after={ev.after} />
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>
                    <div style={{ fontWeight: 600 }}>{ev.userName}</div>
                    <div>{formatTimestamp(ev.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Agency confirmation section */}
          <div style={agencyBlockStyle} data-testid="agency-confirmation-section">
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              Agency Confirmation
            </div>

            {confirmedAt ? (
              <div
                data-testid="agency-confirmed-date"
                style={{ fontSize: 13, color: 'var(--green, #15803d)' }}
              >
                Recorded confirmation date: {formatDateOnly(confirmedAt)}
              </div>
            ) : (
              <div data-testid="agency-confirmation-unrecorded" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                No agency confirmation is recorded. Confirmation cannot be entered from this panel.
                Technical delivery acknowledgments do not establish regulatory acceptance.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Slide-in animation keyframe injected once */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
